/**
 * Registro público de empresas — MVP v1
 * (Carlos jun 28, 2026)
 *
 * Form simple: 6 campos → POST /empresas/register → token → redirige a /empresas/docs
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import empresaApi from '../utils/empresaApi';
import { BRAND } from '../brand';

const EmpresaRegistro = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    razon_social: '',
    rut: '',
    rep_legal_name: '',
    email: '',
    phone: '+56',
    password: '',
    password2: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.password2) {
      setError('Las contraseñas no coinciden');
      return;
    }
    if (form.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    setLoading(true);
    try {
      const res = await empresaApi.post('/empresas/register', {
        razon_social: form.razon_social.trim(),
        rut: form.rut.trim(),
        rep_legal_name: form.rep_legal_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        password: form.password,
      });
      localStorage.setItem('empresa_token', res.data.access_token);
      navigate('/empresas/docs');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg).join(', ')
            : 'Error al registrar la empresa'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="text-sm text-emerald-700 hover:underline" data-testid="emp-back-home">
          ← Volver al inicio
        </Link>
        <div className="mt-4 bg-white rounded-2xl shadow-lg border border-slate-200 p-6 sm:p-8">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-black text-slate-900">🏢 Registro de Empresa</h1>
            <p className="text-sm text-slate-600 mt-2">
              Plataforma para empresas que envían dinero a Bolivia con comisión preferente.
              <br />
              <span className="text-xs text-slate-500">
                Tras el registro, deberás subir 3 documentos. Tu cuenta queda activa cuando el equipo
                de {BRAND.name} los aprueba.
              </span>
            </p>
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid="emp-register-form">
            <Field label="Razón social *" id="razon_social" value={form.razon_social} onChange={upd('razon_social')} placeholder="Ej: Comercializadora del Sur SPA" required testid="emp-razon-social" />
            <Field label="RUT empresa *" id="rut" value={form.rut} onChange={upd('rut')} placeholder="Ej: 78.002.172-1" required testid="emp-rut" />
            <Field label="Nombre del representante legal *" id="rep_legal_name" value={form.rep_legal_name} onChange={upd('rep_legal_name')} placeholder="Nombre completo" required testid="emp-rep-legal" />
            <Field label="Email corporativo *" id="email" type="email" value={form.email} onChange={upd('email')} placeholder="contacto@miempresa.cl" required testid="emp-email" />
            <Field label="Teléfono *" id="phone" value={form.phone} onChange={upd('phone')} placeholder="+56 9 1234 5678" required testid="emp-phone" />
            <Field label="Contraseña * (mín 8 caracteres)" id="password" type="password" value={form.password} onChange={upd('password')} required testid="emp-password" />
            <Field label="Confirmar contraseña *" id="password2" type="password" value={form.password2} onChange={upd('password2')} required testid="emp-password2" />

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded p-3" data-testid="emp-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="emp-submit-register"
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold py-3 rounded-lg text-base mt-2"
            >
              {loading ? 'Registrando…' : 'Crear cuenta de empresa'}
            </button>

            <p className="text-center text-xs text-slate-600 pt-2">
              ¿Ya tienes cuenta?{' '}
              <Link to="/empresas/login" className="text-emerald-700 hover:underline font-medium" data-testid="emp-go-login">
                Inicia sesión
              </Link>
            </p>
          </form>
        </div>

        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-900">
          <p className="font-semibold mb-1">🛡️ Cumplimiento UAF</p>
          <p>
            {BRAND.name} opera 100% legal en Chile (Sujeto Obligado UAF — Ley 19.913). Los datos
            que entregas son tratados conforme a la Ley 19.628 de Protección de Datos.
          </p>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, id, type = 'text', value, onChange, placeholder, required, testid }) => (
  <label className="block">
    <span className="text-xs font-semibold text-slate-700 mb-1 block">{label}</span>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      data-testid={testid}
      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400 focus:border-emerald-500 outline-none"
    />
  </label>
);

export default EmpresaRegistro;
